<template>
  <div class="cleanup-dialog-box">

    <el-form
      v-if="!allBackupsEncryptedWarningVisible"
      :model="cleanupForm"
      @submit="onSubmit">
      <el-dialog
        width-size="large"
        :title="$t('wrh-dashboard.cleanup-dialog-box.title', {brandName: getBrandName})"
        @close="onClose"
        :show-close="false"
        visible
      >
        <div class="mx-24 my-24">

          <div class="el-text el-text--body-24">
            {{ $t('wrh-dashboard.cleanup-dialog-box.message', {brandName: getBrandName}) }}
          </div>

          <el-form-item
            style="margin-top: 32px;">

            <el-row
              type="flex"
              style="align-items: center;"
            >
              <el-col
                style="text-align: left;"
                :span="16">

                <el-form-item>
                  <el-checkbox v-model="cleanupForm.deleteOlderThan">
                    {{ $t('wrh-dashboard.cleanup-dialog-box.checkbox.delete-versions-older-than') }}
                  </el-checkbox>
                </el-form-item>

              </el-col>
              <el-col
                style="text-align: left;"
                :span="3">
                <el-form-item>
                  <el-input
                    v-model="cleanupForm.olderThan"
                    style="width: 55px;"
                    size="small"
                    placeholder=""
                    type="number"
                    min="1"
                    :max="olderThanMax"
                    :disabled="!cleanupForm.deleteOlderThan"
                  />
                </el-form-item>

              </el-col>

              <el-col
                style="text-align: left;"
                :span="5">

                <el-form-item>
                  <el-combobox
                    v-model="olderThanTimeUnitId"
                    :disabled="!cleanupForm.deleteOlderThan"
                    @change="onOlderThanTimeUnitChanged"
                    size="small">
                    <el-option
                      v-for="item in olderThanTimeUnitList"
                      :key="item.id"
                      :label="item.label"
                      :value="item.id"/>
                  </el-combobox>
                </el-form-item>
              </el-col>

            </el-row>

            <el-row
              type="flex"
              style="align-items: center;"
              v-if="!isArpfBackup"
              class="pb-0 mb-0">
              <el-col
                style="text-align: left;"
                :span="16">
                <el-form-item>
                  <el-checkbox v-model="cleanupForm.deleteAllExcept" >
                    {{ $t('wrh-dashboard.cleanup-dialog-box.checkbox.delete-all-versions-except') }}
                  </el-checkbox>
                </el-form-item>
              </el-col>

              <el-col
                style="text-align: left;"
                :span="3">
                <el-form-item>
                  <el-input
                    v-model="cleanupForm.exceptVersions"
                    style="width: 55px;"
                    size="small"
                    placeholder=""
                    type="number"
                    min="1"
                    max="99"
                    :disabled="!cleanupForm.deleteAllExcept"
                  />
                </el-form-item>
              </el-col>

              <el-col
                :span="5"
                style="text-align: left;">
                <el-form-item>
                  <span class="el-text el-text--body-24">
                    {{ $t('wrh-dashboard.cleanup-dialog-box.checkbox.delete-all-versions-recent-ones') }}
                  </span>
                </el-form-item>
              </el-col>

            </el-row>

          </el-form-item>

          <el-card
            v-if="encryptedWarningVisible"
            border-color="fixed-warning"
            class="cleanup-dialog-box-encrypted-warning"
          >
            <el-row
              type='flex'
              justify="center"
              align="middle"
              class="py-16 pr-24">
              <el-col
                :span="2"
                style="text-align: center;  line-height: 100%;">
                <el-icon
                  color="fixed-warning"
                  name="i-exclamation-triangle--16"
                />
              </el-col>
              <el-col :span="22">
                {{ $t('wrh-dashboard.cleanup-dialog-box.message.cleanup-encrypted', {
                  encryptedBackupCount: encryptedBackupCount,
                  totalBackupCount: totalBackupCount
                }) }}
              </el-col>
            </el-row>
          </el-card>
        </div>

        <template
          v-if="isCleanupAll"
          #footer-aside>
          <el-button
            v-if="encryptedWarningVisible"
            @click="onGoToBackups"
            type="ghost"
          >
            {{ $t('wrh-dashboard.form.button.go-to-backups') }}
          </el-button>
        </template>

        <template slot="footer">
          <el-button
            @click="onCancel"
            native-type="button"
            type="secondary"
          >
            {{ $t('wrh-dashboard.form.button.cancel') }}
          </el-button>
          <el-button
            native-type="submit"
            :loading="isInProgress"
            :disabled="!isAnyOptionSelected || isInProgress"
          >
            {{
              $t('wrh-dashboard.cleanup-dialog-box.button.cleanup-now')
            }}
          </el-button>
        </template>

      </el-dialog>
    </el-form>

    <el-dialog
      v-if="allBackupsEncryptedWarningVisible"
      :title="$t('wrh-dashboard.cleanup-encrypted-dialog-box.title', {brandName: getBrandName})"
      @close="onClose"
      :show-close="false"
      visible>

      <div class="mx-24 my-24 el-text el-text--size-xs">
        <div style="margin-top: 13px; margin-bottom: -4px;">
          {{ $t('wrh-dashboard.cleanup-encrypted-dialog-box.message') }}
        </div>
      </div>

      <template #footer>
        <el-button
          @click="onCancel"
          native-type="button"
          type="secondary">
          {{ $t('wrh-dashboard.form.button.cancel') }}
        </el-button>

        <el-button
          native-type="button"
          @click="onGoToBackups">
          {{ $t('wrh-dashboard.form.button.go-to-backups') }}
        </el-button>

      </template>
    </el-dialog>


  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import {
  PENDING_STATUS
} from '../store/action-status';

const { mapGetters } = createNamespacedHelpers('wrh-dashboard');

export default {
  name: 'CleanupDialogBox',
  props: {
    isCleanupAll: {
      type: Boolean,
      default: true,
      required: false
    },
    cleanupBox: {
      type: Object,
      default: () => ({}),
      required: false
    }
  },
  data() {
    return {
      cleanupForm: {
        deleteOlderThan: false,
        deleteAllExcept: false,
        olderThan: 30,
        exceptVersions: 10
      },
      olderThanTimeUnitId: 0,
      olderThanTimeUnitList: [
        { id: 0, label: this.$t('wrh-dashboard.cleanup-dialog-box.combobox.time-unit.days') },
        { id: 1, label: this.$t('wrh-dashboard.cleanup-dialog-box.combobox.time-unit.months') },
        { id: 2, label: this.$t('wrh-dashboard.cleanup-dialog-box.combobox.time-unit.years') }
      ],
      backupList: [],
      totalBackupCount: 0,
      encryptedBackupCount: 0
    };
  },
  computed: {
    ...mapGetters([
      'getCleanupStatus',
      'getBrandName',
      'getNavMenuItems'
    ]),
    isInProgress() {
      return this.getCleanupStatus === PENDING_STATUS;
    },
    isAnyOptionSelected() {
      return this.cleanupForm.deleteOlderThan || this.cleanupForm.deleteAllExcept;
    },
    isArpfBackup() {
      return this.isCleanupAll ? false : this.cleanupBox.type === 'ARPF_BACKUP';
    },
    olderThanMax() {
      return this.getOlderThanMaxValue(this.olderThanTimeUnitId);
    },
    encryptedWarningVisible() {
      return this.isCleanupAll &&
        this.encryptedBackupCount > 0;
    },
    allBackupsEncryptedWarningVisible() {
      return this.isCleanupAll &&
        this.encryptedBackupCount > 0 &&
        this.totalBackupCount === this.encryptedBackupCount;
    }
  },
  methods: {
    onGoToBackups() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: this.getAnalyticsCleanupEventCategory(),
        eventAction: 'Go_to_backups',
        eventLabel: ''
      });

      this.$emit('goToBackups');
      this.onClose();
    },
    getOlderThanApproximateDays() {
      if (this.cleanupForm.deleteOlderThan) {
        const value = this.cleanupForm.olderThan;
        const timeUnit = this.olderThanTimeUnitId;
        if (timeUnit === 0) {
          return value;
        } else if (timeUnit === 1) {
          return value * 30;
        } else if (timeUnit === 2) {
          return value * 365;
        }
      }
      return 0;
    },
    getAnalyticsCleanupEventCategory() {
      return this.isCleanupAll ? 'WR_cloud_cleanup' : 'WR_backups';
    },
    onSubmit(e) {
      if (this.cleanupForm.deleteOlderThan) {
        const olderThanDays = this.getOlderThanApproximateDays();
        this.$gtm.push({
          event: 'Acronis',
          eventCategory: this.getAnalyticsCleanupEventCategory(),
          eventAction: 'Delete_older_than',
          eventLabel: olderThanDays
        });
      }

      if (this.cleanupForm.deleteAllExcept) {
        this.$gtm.push({
          event: 'Acronis',
          eventCategory: this.getAnalyticsCleanupEventCategory(),
          eventAction: 'Delete_old_version',
          eventLabel: this.cleanupForm.exceptVersions
        });
      }

      this.$emit('submitCleanup', {
        cleanupAll: this.isCleanupAll,
        archiveId: this.isCleanupAll ? '' : this.cleanupBox.id,
        boxType: this.isCleanupAll ? '' : this.cleanupBox.type,
        boxDisplayName: this.isCleanupAll ? '' : this.cleanupBox.displayName,
        deleteOlderThan: this.cleanupForm.deleteOlderThan,
        deleteAllExcept: this.cleanupForm.deleteAllExcept,
        olderThan: this.cleanupForm.olderThan,
        olderThanTimeUnitId: this.olderThanTimeUnitId,
        exceptVersions: this.cleanupForm.exceptVersions
      });
    },
    onCancel() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: this.getAnalyticsCleanupEventCategory(),
        eventAction: 'Cleanup_cancel',
        eventLabel: ''
      });

      this.onClose();
    },
    onClose() {
      this.$emit('handleClose');
    },
    onOlderThanTimeUnitChanged(value) {
      const maxValue = this.getOlderThanMaxValue(value);
      if (this.cleanupForm.olderThan > maxValue) {
        this.cleanupForm.olderThan = maxValue;
      }
    },
    getOlderThanMaxValue(unitId) {
      if (unitId === 0) {
        return 1095;
      } else if (unitId === 1) {
        return 36;
      } else if (unitId === 2) {
        return 3;
      }
      return 30;
    }
  },
  mounted() {
    if (this.isCleanupAll) {
      this.backupList = this.getNavMenuItems.filter(item => item.group === 'BACKUPS');
      this.totalBackupCount = this.backupList.length;
      this.encryptedBackupCount =
          this.backupList.filter(item => item.options.passwordRequired === true).length;
    }
  }
};
</script>

<style lang="scss" scoped>
  .cleanup-dialog-box-encrypted-warning {
    background-color: $av-fixed-warning-accent;
  }
</style>
